import { createRoute, redirect, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { IconChevronRight, IconSearch } from '@tabler/icons-react';
import { Route as appRoute } from '../../../../app.js';
import { useAuthStore } from '../../../../../lib/auth.js';
import { cn } from '../../../../../lib/cn.js';
import {
  useTenantSharing,
  useSetCourseVisibility,
  useSetProviderVisibility,
  type SharingCourse,
  type SharingProvider,
} from '../../../../../hooks/useSharing.js';
import { AdminPageHeader } from '../../../../../components/admin/AdminPageHeader.js';
import { Checkbox } from '../../../../../components/ui/Checkbox.js';
import { Input } from '../../../../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/tenants/$tenantId/sharing',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: SharingPage,
});

function VisibilityTag({ visible }: { visible: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={
        visible
          ? { backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }
          : { backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }
      }
    >
      {visible ? 'Visible' : 'Hidden'}
    </span>
  );
}

const CURATION_STYLES: Record<string, { bg: string; color: string; border: string; label: string }> = {
  curated: { bg: '#E8F3EC', color: '#1E7A47', border: '#C9E5D2', label: 'Curated' },
  needs_review: { bg: '#FBF1E3', color: '#9A6B16', border: '#EBD9BD', label: 'Needs review' },
  shell: { bg: '#F0F0F2', color: '#6B6B6B', border: '#E0E0E4', label: 'Shell' },
  archived: { bg: '#F0F0F2', color: '#6B6B6B', border: '#E0E0E4', label: 'Archived' },
};

function CurationBadge({ state }: { state: string }) {
  const s = CURATION_STYLES[state] ?? { bg: '#F0F0F2', color: '#6B6B6B', border: '#E0E0E4', label: state };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {s.label}
    </span>
  );
}

/** Where a course's effective visibility comes from (precedence: course > provider > default). */
function courseSource(course: SharingCourse, providerRule: boolean | null): string {
  if (course.rule !== null) return 'Course rule';
  if (providerRule !== null) return 'Provider rule';
  return 'Agency default';
}

function CourseRow({
  tenantId,
  course,
  providerRule,
}: {
  tenantId: string;
  course: SharingCourse;
  providerRule: boolean | null;
}) {
  const setVisibility = useSetCourseVisibility(tenantId);
  // Consultants only see a course that is BOTH shared/visible AND curated.
  const sharedButUncurated = course.effective_visible && course.curation_state !== 'curated';
  return (
    <tr className="hover:bg-cream/30 transition-colors">
      <td className="px-5 py-2.5 pl-10 text-ink">
        {course.name}
        {course.cricos_code && (
          <span className="ml-2 text-[11px] text-muted font-mono">CRICOS {course.cricos_code}</span>
        )}
        {sharedButUncurated && (
          <div className="text-[11px]" style={{ color: '#9A6B16' }}>
            Shared, but hidden from consultants until curated.
          </div>
        )}
      </td>
      <td className="px-5 py-2.5 text-[11px] text-muted">{courseSource(course, providerRule)}</td>
      <td className="px-5 py-2.5">
        <CurationBadge state={course.curation_state} />
      </td>
      <td className="px-5 py-2.5">
        <VisibilityTag visible={course.effective_visible} />
      </td>
      <td className="px-5 py-2.5 text-right">
        <Checkbox
          checked={course.effective_visible}
          disabled={setVisibility.isPending}
          onChange={(e) => setVisibility.mutate({ courseId: course.id, visible: e.target.checked })}
          label="Visible"
        />
      </td>
    </tr>
  );
}

function ProviderBlock({
  tenantId,
  provider,
  courses,
  forceOpen,
}: {
  tenantId: string;
  provider: SharingProvider;
  courses: SharingCourse[];
  forceOpen: boolean;
}) {
  const setVisibility = useSetProviderVisibility(tenantId);
  const [open, setOpen] = useState(false);
  const expanded = forceOpen || open;

  return (
    <div className="bg-white border border-line rounded-xl overflow-hidden">
      <div className="flex items-center justify-between bg-cream/60 border-b border-line px-5 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
          aria-expanded={expanded}
        >
          <IconChevronRight
            size={16}
            className={cn('shrink-0 text-muted transition-transform', expanded && 'rotate-90')}
          />
          <div className="min-w-0">
            <div className="font-medium text-ink truncate">{provider.name}</div>
            <div className="text-[11px] text-muted">
              {courses.length} course{courses.length === 1 ? '' : 's'} &middot;{' '}
              {provider.rule !== null ? 'provider rule set' : 'follows agency default'}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-3 shrink-0">
          <VisibilityTag visible={provider.effective_visible} />
          <Checkbox
            checked={provider.effective_visible}
            disabled={setVisibility.isPending}
            onChange={(e) => setVisibility.mutate({ providerId: provider.id, visible: e.target.checked })}
            label="All courses"
          />
        </div>
      </div>

      {expanded &&
        (courses.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted">No courses for this provider.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-line">
              {courses.map((c) => (
                <CourseRow key={c.id} tenantId={tenantId} course={c} providerRule={provider.rule} />
              ))}
            </tbody>
          </table>
        ))}
    </div>
  );
}

const CURATION_FILTERS = ['all', 'curated', 'needs_review', 'shell', 'archived'] as const;
type CurationFilter = (typeof CURATION_FILTERS)[number];

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs font-medium border transition',
        active
          ? 'bg-navy text-cream border-navy'
          : 'bg-white text-ink-soft border-line hover:border-navy/40 hover:text-navy',
      )}
    >
      {children}
    </button>
  );
}

function SharingPage() {
  const { tenantId } = useParams({ from: '/app/admin/tenants/$tenantId/sharing' });
  const sharing = useTenantSharing(tenantId);

  const [query, setQuery] = useState('');
  const [curation, setCuration] = useState<CurationFilter>('all');

  const q = query.trim().toLowerCase();
  const hasFilter = q.length > 0 || curation !== 'all';

  const groups = useMemo(() => {
    if (!sharing.data) return [];
    return sharing.data.providers
      .map((provider) => {
        const providerNameMatches = !!q && provider.name.toLowerCase().includes(q);
        const courses = provider.courses.filter((c) => {
          const textOk =
            !q ||
            providerNameMatches ||
            c.name.toLowerCase().includes(q) ||
            (c.cricos_code ?? '').toLowerCase().includes(q);
          const curationOk = curation === 'all' || c.curation_state === curation;
          return textOk && curationOk;
        });
        return { provider, courses };
      })
      .filter(({ courses }) => !hasFilter || courses.length > 0);
  }, [sharing.data, q, curation, hasFilter]);

  return (
    <div>
      <AdminPageHeader
        title={sharing.data ? `Sharing — ${sharing.data.tenant.name}` : 'Sharing'}
        description="Toggle a whole provider, or individual courses. A course rule overrides its provider's setting; with no rule, a course follows the provider, then the agency default. Consultants only see courses that are both visible here AND curated."
        backTo="/app/admin/tenants"
        backLabel="All tenants"
      />

      {sharing.isLoading && <p className="text-muted">Loading…</p>}
      {sharing.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load sharing: {(sharing.error as Error).message}
        </p>
      )}

      {sharing.data && (
        <div className="space-y-5">
          <div
            className="rounded-lg px-4 py-3 text-[13px]"
            style={{ backgroundColor: '#FBF8F2', color: '#6B6B6B', border: '1px solid #E8E2D5' }}
          >
            Agency default:{' '}
            <strong>
              {sharing.data.tenant.default_master_visibility
                ? 'all master courses visible'
                : 'master courses hidden'}
            </strong>{' '}
            unless overridden below.
          </div>

          {/* Search + curation filter — keeps the screen usable across all providers/courses. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative max-w-xs">
              <IconSearch
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search provider or course…"
                className="pl-9"
                aria-label="Search providers and courses"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {CURATION_FILTERS.map((f) => (
                <FilterChip key={f} active={curation === f} onClick={() => setCuration(f)}>
                  {f === 'all' ? 'All' : (CURATION_STYLES[f]?.label ?? f)}
                </FilterChip>
              ))}
            </div>
          </div>

          {groups.length === 0 ? (
            <p className="text-sm text-muted px-1">No providers or courses match your filters.</p>
          ) : (
            groups.map(({ provider, courses }) => (
              <ProviderBlock
                key={provider.id}
                tenantId={tenantId}
                provider={provider}
                courses={courses}
                forceOpen={hasFilter}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
